import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableFooter from '@mui/material/TableFooter';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import type { ColumnsType, ColumnType } from './tableTypes';
import {
  TABLE_BODY_BG,
  TABLE_CELL_BORDER,
  TABLE_CONTROL_BORDER,
  TABLE_CONTROL_BORDER_HOVER,
  TABLE_HEAD_BG,
  TABLE_HEAD_BORDER,
  TABLE_PAGINATION_ACTIVE_BG,
  TABLE_PAGINATION_ACTIVE_HOVER,
  TABLE_PAGINATION_BG,
  TABLE_PAGINATION_ITEM_RADIUS_PX,
  TABLE_ROW_EXPANDED_BG,
  TABLE_WRAPPER_BORDER,
  tableFixedEdgeInsetShadow,
} from '../components/common/tableList/tableListTokens';
import { TABLE_LEADING_WIDTHS } from '../components/common/tableList/tableListSizing';
import { muahangKiotLike } from '../components/layout/muahangKiotTokens';
import {
  KiotExpandViewportBorderPortal,
  kiotExpandDataRowAttrs,
} from '../components/common/kiotListExpand';

const KiotAntExpandPanel: React.FC<{
  colSpan: number;
  scrollEl: HTMLDivElement | null;
  viewportW: number;
  anchorRowEl: HTMLTableRowElement | null;
  children: React.ReactNode;
}> = ({ colSpan, scrollEl, viewportW, anchorRowEl, children }) => {
  const [contentEl, setContentEl] = useState<HTMLElement | null>(null);
  return (
    <TableRow className="kiot-list-expand-panel">
      <TableCell
        colSpan={colSpan}
        sx={{
          py: 0,
          px: 0,
          border: 'none',
          bgcolor: 'transparent',
          verticalAlign: 'top',
          lineHeight: 0,
        }}
      >
        <KiotExpandViewportBorderPortal
          active
          scrollContainer={scrollEl}
          anchorRowEl={anchorRowEl}
          contentEl={contentEl}
          widthPx={viewportW > 0 ? viewportW : undefined}
        />
        <Box
          ref={setContentEl}
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'sticky',
            left: 0,
            zIndex: 2,
            width: viewportW > 0 ? viewportW : '100%',
            maxWidth: viewportW > 0 ? viewportW : '100%',
            boxSizing: 'border-box',
            bgcolor: '#fff',
            overflow: 'hidden',
            lineHeight: 'normal',
          }}
        >
          {children}
        </Box>
      </TableCell>
    </TableRow>
  );
};

export type { ColumnsType } from './tableTypes';

function getByPath(record: any, dataIndex?: string | string[]) {
  if (!dataIndex) return undefined;
  const path = Array.isArray(dataIndex) ? dataIndex : dataIndex.split('.');
  return path.reduce((acc, key) => (acc == null ? acc : acc[key]), record);
}

function flattenColumns(cols: ColumnType[]): ColumnType[] {
  const out: ColumnType[] = [];
  cols.forEach((col) => {
    if (col.children?.length) {
      out.push(...flattenColumns(col.children));
    } else if (!col.hidden) {
      out.push(col);
    }
  });
  return out;
}

function headerRows(cols: ColumnType[]): ColumnType[][] {
  const hasNested = cols.some((c) => c.children?.length);
  if (!hasNested) return [flattenColumns(cols)];
  const row1: ColumnType[] = [];
  const row2: ColumnType[] = [];
  cols.forEach((col) => {
    if (col.hidden) return;
    if (col.children?.length) {
      row1.push({ ...col, colSpan: col.children.length, rowSpan: 1 });
      col.children.forEach((ch) => row2.push(ch));
    } else {
      row1.push({ ...col, rowSpan: 2 });
    }
  });
  return [row1, row2.filter((c) => !c.hidden)];
}

export interface TablePaginationConfig {
  current?: number;
  page?: number;
  pageSize?: number;
  total?: number;
  onChange?: (page: number, pageSize?: number) => void;
  showSizeChanger?: boolean;
  pageSizeOptions?: (string | number)[];
  showTitle?: boolean;
  position?: ('topLeft' | 'topCenter' | 'topRight' | 'bottomLeft' | 'bottomCenter' | 'bottomRight')[];
  showTotal?: (total: number, range?: [number, number]) => React.ReactNode;
  [key: string]: any;
}

export interface TableProps<T = any> {
  columns?: ColumnsType<T>;
  dataSource?: T[];
  rowKey?: string | ((record: T) => string);
  pagination?: false | TablePaginationConfig;
  loading?: boolean;
  scroll?: { x?: number | string; y?: number | string };
  size?: 'small' | 'middle' | 'large';
  bordered?: boolean;
  style?: React.CSSProperties;
  className?: string;
  rowClassName?: string | ((record: T, index: number) => string);
  rowSelection?: {
    type?: 'checkbox' | 'radio';
    selectedRowKeys?: React.Key[];
    onChange?: (keys: React.Key[], rows: T[]) => void;
    columnWidth?: number;
    getCheckboxProps?: (record: T) => { disabled?: boolean };
  };
  expandable?: {
    expandedRowRender?: (record: T) => React.ReactNode;
    expandedRowKeys?: React.Key[];
    onExpand?: (expanded: boolean, record: T) => void;
    defaultExpandAllRows?: boolean;
    expandRowByClick?: boolean;
  };
  summary?: (data: readonly T[]) => React.ReactNode;
  /** Chỉ dựng cột trong vùng nhìn thấy khi bảng rất nhiều cột. */
  windowColumns?: boolean;
  /**
   * Bỏ qua render dòng khi record / chọn / cột không đổi (bảng nhập nhiều dòng).
   * Chỉ bật khi `columns` được memo đúng: render cột không được đọc state ngoài record.
   */
  memoRows?: boolean;
  onRow?: (record: T, index?: number) => React.HTMLAttributes<HTMLTableRowElement>;
  footer?: () => React.ReactNode;
  components?: any;
  locale?: any;
  showHeader?: boolean;
  title?: () => React.ReactNode;
  [key: string]: any;
}

function parseColWidthPx(width: number | string | undefined, fallback = 120): number {
  if (typeof width === 'number' && Number.isFinite(width)) return Math.max(0, width)
  if (typeof width === 'string') {
    const n = parseFloat(width)
    if (Number.isFinite(n)) return Math.max(0, n)
  }
  return fallback
}

type ColumnWindowItem =
  | { kind: 'col'; index: number }
  | { kind: 'gap'; start: number; span: number; width: number }

type ColumnWindowValue = {
  skip: Set<number>
  gapAt: Map<number, { span: number; width: number }>
}

const ColumnWindowContext = React.createContext<ColumnWindowValue | null>(null)

/** Vị trí sticky ngang của ô tổng theo `index` (0 = cột chọn, i = leaf i - 1) — để ô tổng cột cố định không bị dòng cuộn đè. */
const SummaryStickyContext = React.createContext<((index: number) => React.CSSProperties | undefined) | null>(null)

function buildColumnWindow(
  cols: ColumnType[],
  scrollLeft: number,
  viewport: number,
  leadingLeftPx: number,
): ColumnWindowItem[] {
  const buffer = 180
  const widths = cols.map((col) => parseColWidthPx(col.width))
  const viewStart = Math.max(0, scrollLeft - buffer)
  const viewEnd = scrollLeft + viewport + buffer
  const items: ColumnWindowItem[] = []
  let gapStart = -1
  let gapSpan = 0
  let gapWidth = 0
  let offset = leadingLeftPx
  const flush = () => {
    if (gapSpan <= 0) return
    items.push({ kind: 'gap', start: gapStart, span: gapSpan, width: gapWidth })
    gapSpan = 0
    gapWidth = 0
    gapStart = -1
  }
  cols.forEach((col, index) => {
    const width = widths[index]
    const left = offset
    const right = offset + width
    offset = right
    const fixed = col.fixed === 'left' || col.fixed === true || col.fixed === 'right'
    const visible = fixed || (right > viewStart && left < viewEnd)
    if (visible) {
      flush()
      items.push({ kind: 'col', index })
      return
    }
    if (gapSpan === 0) gapStart = index
    gapSpan += 1
    gapWidth += width
  })
  flush()
  return items
}

/**
 * Offset sticky cho cụm fixed left (tiền tố) / fixed right (hậu tố) liên tiếp.
 * `leadingLeftPx`: width cột expand/selection nằm trước leaf columns.
 */
function buildFixedOffsets(
  cols: ColumnType[],
  leadingLeftPx = 0,
): { leftByIndex: Map<number, number>; rightByIndex: Map<number, number> } {
  const leftByIndex = new Map<number, number>()
  const rightByIndex = new Map<number, number>()

  let leftOffset = leadingLeftPx
  for (let i = 0; i < cols.length; i += 1) {
    const fixed = cols[i].fixed
    if (fixed !== 'left' && fixed !== true) break
    leftByIndex.set(i, leftOffset)
    leftOffset += parseColWidthPx(cols[i].width)
  }

  let rightOffset = 0
  for (let i = cols.length - 1; i >= 0; i -= 1) {
    if (cols[i].fixed !== 'right') break
    rightByIndex.set(i, rightOffset)
    rightOffset += parseColWidthPx(cols[i].width)
  }

  return { leftByIndex, rightByIndex }
}

/** Index cột mép trái của cụm fixed right (cột sticky right ngoài cùng bên trái). */
function findStickyRightEdgeIndex(cols: ColumnType[]): number | null {
  let edge: number | null = null
  for (let i = cols.length - 1; i >= 0; i -= 1) {
    if (cols[i].fixed !== 'right') break
    edge = i
  }
  return edge
}

/** Index cột mép phải của cụm fixed left (cột sticky left ngoài cùng bên phải). */
function findStickyLeftEdgeIndex(cols: ColumnType[]): number | null {
  let edge: number | null = null
  for (let i = 0; i < cols.length; i += 1) {
    const fixed = cols[i].fixed
    if (fixed !== 'left' && fixed !== true) break
    edge = i
  }
  return edge
}

/** Kẻ dọc trái cụm sticky right — inset vì border thật bị che khi scroll ngang. */
function stickyFixedRightEdgeShadow(): string {
  return `${tableFixedEdgeInsetShadow('left')}, -10px 0 16px -8px rgba(0,0,0,0.07)`
}

/** Kẻ dọc giữa các cột sticky right (không phải mép ngoài). */
function stickyFixedRightInnerShadow(dividerColor: string): string {
  return `inset 1px 0 0 0 ${dividerColor}`
}

/** Kẻ dọc phải cụm sticky left (mép giáp vùng cuộn). */
function stickyFixedLeftEdgeShadow(): string {
  return `${tableFixedEdgeInsetShadow('right')}, 10px 0 16px -8px rgba(0,0,0,0.07)`
}

/** Kẻ dọc giữa các cột sticky left (không phải mép ngoài). */
function stickyFixedLeftInnerShadow(dividerColor: string): string {
  return `inset -1px 0 0 0 ${dividerColor}`
}

const ALLOT_BAND_BG = '#e8f1fc'
const ALLOT_BAND_TOP = 28

function cellStyle(
  col: ColumnType,
  fixed: 'left' | 'right' | boolean | undefined,
  stickyOffset?: number,
  opts?: { stickyRightEdge?: boolean; stickyLeftEdge?: boolean; isHead?: boolean },
): React.CSSProperties {
  const st: React.CSSProperties = {
    textAlign: col.align || 'left',
    width: col.width,
    minWidth: col.width,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  }
  if (col.ellipsis) {
    st.maxWidth = col.width
    st.overflow = 'hidden'
    st.textOverflow = 'ellipsis'
    st.whiteSpace = 'nowrap'
  }
  if (fixed === 'left' || fixed === true) {
    st.position = 'sticky'
    st.left = stickyOffset ?? 0
    st.zIndex = 2
    st.background = opts?.isHead ? TABLE_HEAD_BG : '#fff'
    const divider = opts?.isHead ? TABLE_HEAD_BORDER : TABLE_CELL_BORDER
    st.boxShadow = opts?.stickyLeftEdge
      ? stickyFixedLeftEdgeShadow()
      : stickyFixedLeftInnerShadow(divider)
  }
  if (fixed === 'right') {
    st.position = 'sticky'
    st.right = stickyOffset ?? 0
    st.zIndex = 2
    st.background = opts?.isHead ? TABLE_HEAD_BG : '#fff'
    const divider = opts?.isHead ? TABLE_HEAD_BORDER : TABLE_CELL_BORDER
    // Mọi cột sticky right cần kẻ trái — border-collapse/stack hay mất đường kẻ giữa các cột cố định.
    st.boxShadow = opts?.stickyRightEdge
      ? stickyFixedRightEdgeShadow()
      : stickyFixedRightInnerShadow(divider)
  }
  return st
}

const SummaryCell: React.FC<{ index?: number; className?: string; children?: React.ReactNode; colSpan?: number; align?: string; style?: React.CSSProperties }> = ({
  index,
  className,
  children,
  colSpan,
  align,
  style,
}) => {
  const columnWindow = useContext(ColumnWindowContext)
  const stickyOf = useContext(SummaryStickyContext)
  if (columnWindow && typeof index === 'number' && index > 0) {
    const leafIndex = index - 1
    if (columnWindow.skip.has(leafIndex)) {
      const gap = columnWindow.gapAt.get(leafIndex)
      if (!gap) return null
      return (
        <TableCell
          colSpan={gap.span}
          sx={{ width: gap.width, minWidth: gap.width, maxWidth: gap.width, p: 0 }}
        />
      )
    }
  }
  return (
    <TableCell
      data-index={index}
      className={className}
      colSpan={colSpan}
      align={align as any}
      sx={style}
      style={typeof index === 'number' ? stickyOf?.(index) : undefined}
    >
      {children}
    </TableCell>
  )
}

const SummaryRow: React.FC<{ children?: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <TableRow sx={style}>{children}</TableRow>
);

const Summary: React.FC<{ fixed?: string; children?: React.ReactNode }> & {
  Row: typeof SummaryRow;
  Cell: typeof SummaryCell;
} = ({ children }) => <>{children}</>;
Summary.Row = SummaryRow;
Summary.Cell = SummaryCell;

const MemoRow = React.memo(
  ({ render }: { render: () => React.ReactNode; deps: unknown[] }) => <>{render()}</>,
  (prev, next) => prev.deps.length === next.deps.length && prev.deps.every((dep, i) => Object.is(dep, next.deps[i])),
);

function TableInner<T extends Record<string, any>>(props: TableProps<T>) {
  const {
    columns = [],
    dataSource = [],
    rowKey = 'key',
    pagination,
    loading,
    scroll,
    size,
    bordered,
    style,
    className,
    rowClassName,
    rowSelection,
    expandable,
    summary,
    windowColumns,
    memoRows,
    onRow,
    footer,
  } = props;

  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const [viewportW, setViewportW] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const scrollLeftRef = useRef(0);
  const scrollRafRef = useRef(0);
  const expandAnchorRowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
  const [, setExpandAnchorVersion] = useState(0);

  const scrollContainerRef = useCallback((el: HTMLDivElement | null) => {
    setScrollEl(el);
  }, []);

  useEffect(() => {
    if (!scrollEl) {
      setViewportW(0);
      return;
    }
    const measure = () => {
      const w = Math.floor(scrollEl.clientWidth);
      setViewportW((prev) => (prev === w ? prev : w));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(scrollEl);
    return () => ro.disconnect();
  }, [scrollEl]);

  const leafCols = useMemo(() => flattenColumns(columns), [columns]);
  const headRows = useMemo(() => headerRows(columns), [columns]);
  const hasSubHeader = leafCols.some((col) => col.subHeader != null);

  const expandColWidth = expandable?.expandedRowRender ? 32 : 0;
  const selectionColWidth = rowSelection
    ? Number(rowSelection.columnWidth ?? TABLE_LEADING_WIDTHS.checkbox)
    : 0;
  const leadingLeftPx = expandColWidth + selectionColWidth;
  const columnWindowItems = useMemo(() => {
    if (!windowColumns || leafCols.length < 40) return null
    return buildColumnWindow(leafCols, scrollLeft, viewportW || 1100, leadingLeftPx)
  }, [leafCols, leadingLeftPx, scrollLeft, viewportW, windowColumns]);
  const columnWindow = useMemo<ColumnWindowValue | null>(() => {
    if (!columnWindowItems) return null
    const skip = new Set<number>()
    const gapAt = new Map<number, { span: number; width: number }>()
    columnWindowItems.forEach((item) => {
      if (item.kind !== 'gap') return
      gapAt.set(item.start, { span: item.span, width: item.width })
      for (let i = 0; i < item.span; i += 1) skip.add(item.start + i)
    })
    return { skip, gapAt }
  }, [columnWindowItems]);

  const onTableScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!windowColumns) return
    scrollLeftRef.current = event.currentTarget.scrollLeft
    if (scrollRafRef.current) return
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = 0
      const next = scrollLeftRef.current
      setScrollLeft((prev) => (Math.abs(prev - next) < 32 ? prev : next))
    })
  }

  const fixedOffsets = useMemo(
    () => buildFixedOffsets(leafCols, leadingLeftPx),
    [leafCols, leadingLeftPx],
  );

  const stickyRightEdgeIndex = useMemo(() => findStickyRightEdgeIndex(leafCols), [leafCols]);
  const stickyLeftEdgeIndex = useMemo(() => findStickyLeftEdgeIndex(leafCols), [leafCols]);

  const leafIndexByIdentity = useMemo(() => {
    const map = new Map<string, number>();
    leafCols.forEach((col, i) => {
      const id = String(
        col.key ?? (Array.isArray(col.dataIndex) ? col.dataIndex.join('.') : col.dataIndex) ?? i,
      );
      map.set(id, i);
    });
    return map;
  }, [leafCols]);

  const resolveLeafIndex = (col: ColumnType, leafIndex?: number) => {
    if (leafIndex != null) return leafIndex;
    return leafIndexByIdentity.get(
      String(
        col.key ?? (Array.isArray(col.dataIndex) ? col.dataIndex.join('.') : col.dataIndex) ?? '',
      ),
    );
  };

  const resolveStickyOffset = (col: ColumnType, leafIndex?: number) => {
    const idx = resolveLeafIndex(col, leafIndex);
    if (idx == null) return undefined;
    if (col.fixed === 'right') return fixedOffsets.rightByIndex.get(idx);
    if (col.fixed === 'left' || col.fixed === true) return fixedOffsets.leftByIndex.get(idx);
    return undefined;
  };

  const isStickyRightEdge = (col: ColumnType, leafIndex?: number) => {
    const idx = resolveLeafIndex(col, leafIndex);
    return idx != null && stickyRightEdgeIndex === idx;
  };

  const isStickyLeftEdge = (col: ColumnType, leafIndex?: number) => {
    const idx = resolveLeafIndex(col, leafIndex);
    return idx != null && stickyLeftEdgeIndex === idx;
  };

  /** Không có leaf fixed left → cột leading sticky (selection / expand) là mép trái. */
  const leadingEdgeShadow = stickyLeftEdgeIndex == null ? stickyFixedLeftEdgeShadow() : undefined;

  const summaryStickyOf = (index: number): React.CSSProperties | undefined => {
    if (index === 0) {
      return rowSelection ? { left: expandColWidth, zIndex: 4, boxShadow: leadingEdgeShadow } : undefined;
    }
    const leaf = index - 1;
    const col = leafCols[leaf];
    if (!col) return undefined;
    const offset = resolveStickyOffset(col, leaf);
    if (offset == null) return undefined;
    if (col.fixed === 'right') {
      return { right: offset, zIndex: 4, boxShadow: isStickyRightEdge(col, leaf) ? stickyFixedRightEdgeShadow() : undefined };
    }
    return { left: offset, zIndex: 4, boxShadow: isStickyLeftEdge(col, leaf) ? stickyFixedLeftEdgeShadow() : undefined };
  };

  const theadRef = useRef<HTMLTableSectionElement>(null);
  const [headH, setHeadH] = useState(0);
  const hasSummary = Boolean(summary);
  useLayoutEffect(() => {
    const el = theadRef.current;
    if (!el || !hasSummary) return;
    const measure = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      setHeadH((prev) => (prev === h ? prev : h));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasSummary]);

  const getKey = (record: T, index: number) => {
    if (typeof rowKey === 'function') return rowKey(record);
    return record[rowKey] ?? index;
  };

  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    if (expandable?.defaultExpandAllRows) {
      const m: Record<string, boolean> = {};
      dataSource.forEach((r, i) => {
        m[String(getKey(r, i))] = true;
      });
      return m;
    }
    return {};
  });

  // Re-read data-row refs after commit so the border portal gets a non-null anchor
  // (needed on first paint when rows mount already expanded).
  useLayoutEffect(() => {
    setExpandAnchorVersion((v) => v + 1);
  }, [expanded]);

  const [page, setPage] = useState(() =>
    pagination === false || pagination == null ? 1 : pagination.current ?? pagination.page ?? 1
  );
  const [pageSize, setPageSize] = useState(() =>
    pagination === false || pagination == null ? 10 : pagination.pageSize ?? 10
  );

  const pag = pagination === false ? null : pagination;
  const showPag = pag != null;
  const hidePaginationBar = Array.isArray(pag?.position) && pag.position.length === 0;
  const current = pag?.current ?? pag?.page ?? page;
  const ps = pag?.pageSize ?? pageSize;
  const total = pag?.total ?? dataSource.length;

  const pagedData = showPag && !pag?.onChange
    ? dataSource.slice((current - 1) * ps, current * ps)
    : dataSource;

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
    pag?.onChange?.(newPage, ps);
  };

  const handleRowsPerPage = (newSize: number) => {
    setPageSize(newSize);
    pag?.onChange?.(1, newSize);
  };

  const totalPages = Math.max(1, Math.ceil(total / ps) || 1);
  const pageSizeOptions = (pag?.pageSizeOptions || [10, 20, 50, 100]).map(Number);
  const fontPx = `${muahangKiotLike.kiotFontSizePx}px`;

  const pagEl = showPag ? (
    <Box
      sx={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
        minHeight: 44,
        py: 0.75,
        px: { xs: 0.5, sm: 1 },
        borderTop: `1px solid ${TABLE_WRAPPER_BORDER}`,
        backgroundColor: TABLE_PAGINATION_BG,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: '1 1 auto', minWidth: 0 }}>
        {pag?.showSizeChanger !== false ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography
              component="span"
              variant="body2"
              sx={{
                fontSize: fontPx,
                fontFamily: muahangKiotLike.kiotFontStack,
                color: muahangKiotLike.kiotTextSecondary,
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
              Số dòng / trang
            </Typography>
            <FormControl size="small" sx={{ minWidth: 88 }}>
              <Select
                value={ps}
                onChange={(e) => handleRowsPerPage(Number(e.target.value))}
                sx={{
                  height: 28,
                  fontSize: fontPx,
                  fontFamily: muahangKiotLike.kiotFontStack,
                  borderRadius: `${TABLE_PAGINATION_ITEM_RADIUS_PX}px`,
                  bgcolor: TABLE_BODY_BG,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: TABLE_CONTROL_BORDER,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: TABLE_CONTROL_BORDER_HOVER,
                  },
                }}
              >
                {pageSizeOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt} / trang
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        ) : null}
        {totalPages > 1 ? (
          <Pagination
            count={totalPages}
            page={current}
            onChange={handlePageChange}
            color="primary"
            size="small"
            variant="outlined"
            shape="rounded"
            showFirstButton
            showLastButton
            siblingCount={1}
            boundaryCount={1}
            sx={{
              '& .MuiPagination-ul': { flexWrap: 'wrap', gap: 0.25 },
              '& .MuiPaginationItem-root': {
                fontWeight: 500,
                fontSize: fontPx,
                fontFamily: muahangKiotLike.kiotFontStack,
                minWidth: 28,
                height: 28,
                borderRadius: `${TABLE_PAGINATION_ITEM_RADIUS_PX}px`,
                borderColor: TABLE_CONTROL_BORDER,
                color: 'text.primary',
                '&:hover': {
                  borderColor: TABLE_CONTROL_BORDER_HOVER,
                  backgroundColor: 'rgba(0,0,0,0.04)',
                },
                '&.Mui-selected': {
                  fontWeight: 600,
                  color: '#fff !important',
                  backgroundColor: `${TABLE_PAGINATION_ACTIVE_BG} !important`,
                  borderColor: `${TABLE_PAGINATION_ACTIVE_BG} !important`,
                  '&:hover': {
                    backgroundColor: `${TABLE_PAGINATION_ACTIVE_HOVER} !important`,
                    borderColor: `${TABLE_PAGINATION_ACTIVE_HOVER} !important`,
                  },
                },
              },
            }}
          />
        ) : null}
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontSize: fontPx,
          fontFamily: muahangKiotLike.kiotFontStack,
          color: muahangKiotLike.kiotTextSecondary,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        Tổng: {total}
      </Typography>
    </Box>
  ) : null;

  const selectedKeys = rowSelection?.selectedRowKeys ?? [];
  // Dòng memo có thể giữ closure cũ → checkbox đọc lựa chọn / dữ liệu hiện tại qua ref
  const selectedKeysRef = useRef(selectedKeys);
  selectedKeysRef.current = selectedKeys;
  const dataSourceRef = useRef(dataSource);
  dataSourceRef.current = dataSource;

  const toggleExpand = (record: T, index: number) => {
    const k = String(getKey(record, index));
    setExpanded((prev) => {
      const next = { ...prev, [k]: !prev[k] };
      expandable?.onExpand?.(!!next[k], record);
      return next;
    });
  };

  const padding = size === 'small' ? '4px 8px' : '8px 12px';
  const wrapperClass = ['ant-table-wrapper', size === 'small' ? 'ant-table-small' : '', className]
    .filter(Boolean)
    .join(' ');

  const flexFill =
    style?.flex === 1 ||
    style?.flex === '1' ||
    style?.height === '100%' ||
    (typeof style?.display === 'string' && style.display.includes('flex'));

  const summaryNode = summary ? summary(pagedData) : null;
  const summaryTop = React.isValidElement(summaryNode) && (summaryNode.props as { fixed?: string }).fixed === 'top';
  const summaryCellSx = {
    '& > .MuiTableRow-root > .MuiTableCell-root': {
      position: 'sticky',
      zIndex: 3,
      bgcolor: '#f1f5f9',
      fontWeight: 700,
      ...(scroll?.y ? (summaryTop ? { top: headH } : { bottom: 0 }) : {}),
    },
  };

  return (
    <Box
      className={wrapperClass}
      sx={
        flexFill
          ? {
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              overflow: 'hidden',
              ...style,
            }
          : style
      }
    >
      <TableContainer
        ref={scrollContainerRef}
        onScroll={onTableScroll}
        component={Paper}
        variant={bordered ? 'outlined' : 'elevation'}
        className={['ant-table', size === 'small' ? 'ant-table-small' : '', bordered ? 'ant-table-bordered' : '']
          .filter(Boolean)
          .join(' ')}
        sx={{
          ...(flexFill
            ? {
                flex: 1,
                minHeight: 0,
                maxHeight: scroll?.y != null ? scroll.y : 'none',
                overflow: 'auto',
              }
            : { maxHeight: scroll?.y, overflow: 'auto' }),
          position: 'relative',
          '& .MuiTableHead-root .MuiTableCell-root': {
            fontWeight: 600,
            bgcolor: 'var(--muahang-table-head-bg, #f3f5f8)',
            borderBottom: '1px solid var(--muahang-table-head-border, #e4e7ec)',
            borderRight: '1px solid var(--muahang-table-head-border, #e4e7ec)',
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'clip',
            wordBreak: 'break-word',
            px: '8px',
            py: '7px',
          },
          '& .MuiTableHead-root .MuiTableCell-root.ant-table-row-expand-icon-cell': {
            px: 0,
            py: '4px',
          },
          '& .MuiTableHead-root .MuiTableCell-root.ant-table-selection-column': {
            px: '2px',
            py: '4px',
          },
          '& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root:last-of-type': {
            borderRight: 'none',
          },
          '& .MuiTableBody-root .MuiTableCell-root': {
            borderBottom: '1px solid var(--muahang-table-cell-border, #f0f1f3)',
            borderRight: '1px solid var(--muahang-table-cell-border, #f0f1f3)',
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'clip',
            wordBreak: 'break-word',
            px: '8px',
            py: '7px',
          },
          '& .MuiTableBody-root .MuiTableCell-root.ant-table-row-expand-icon-cell': {
            px: 0,
            py: '4px',
          },
          '& .MuiTableBody-root .MuiTableCell-root.ant-table-selection-column': {
            px: '2px',
            py: '4px',
          },
          '& .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root:last-of-type': {
            borderRight: 'none',
          },
          '& .MuiTableBody-root .MuiTableRow-root:hover': {
            bgcolor: 'var(--muahang-row-hover-bg, #f5f7fa)',
          },
          '& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-root.ant-table-row-expand-icon-cell, & .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-root.ant-table-selection-column': {
            bgcolor: 'var(--muahang-row-hover-bg, #f5f7fa)',
          },
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.6)',
              zIndex: 5,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <ColumnWindowContext.Provider value={columnWindow}>
        <MuiTable stickyHeader size={size === 'small' ? 'small' : 'medium'} sx={{ minWidth: scroll?.x }}>
          <TableHead ref={theadRef}>
            {headRows.map((row, ri) => (
              <TableRow key={ri} className={hasSubHeader && ri === 0 ? 'reality-title-band' : undefined}>
                {expandable?.expandedRowRender && ri === 0 && (
                  <TableCell
                    className="ant-table-row-expand-icon-cell"
                    padding="none"
                    sx={{
                      width: expandColWidth,
                      minWidth: expandColWidth,
                      maxWidth: expandColWidth,
                      position: 'sticky',
                      top: 0,
                      left: 0,
                      zIndex: 5,
                      bgcolor: TABLE_HEAD_BG,
                      px: 0,
                      textAlign: 'center',
                      boxShadow: rowSelection ? undefined : leadingEdgeShadow,
                    }}
                  />
                )}
                {rowSelection && ri === 0 && (
                  <TableCell
                    className="ant-table-selection-column"
                    padding="none"
                    sx={{
                      width: selectionColWidth,
                      minWidth: selectionColWidth,
                      maxWidth: selectionColWidth,
                      position: 'sticky',
                      top: 0,
                      left: expandColWidth,
                      zIndex: 5,
                      bgcolor: TABLE_HEAD_BG,
                      px: '2px',
                      py: '4px',
                      textAlign: 'center',
                      boxShadow: leadingEdgeShadow,
                    }}
                  >
                    {rowSelection.type !== 'radio' && (
                      <Checkbox
                        size="small"
                        indeterminate={selectedKeys.length > 0 && selectedKeys.length < dataSource.length}
                        checked={dataSource.length > 0 && selectedKeys.length === dataSource.length}
                        onChange={(e) => {
                          const keys = e.target.checked ? dataSource.map((r, i) => getKey(r, i)) : [];
                          rowSelection.onChange?.(keys, e.target.checked ? dataSource : []);
                        }}
                        sx={{ p: 0, m: 0, color: '#bdbdbd', '&.Mui-checked': { color: 'primary.main' } }}
                      />
                    )}
                  </TableCell>
                )}
                {(columnWindowItems && row.length === leafCols.length
                  ? columnWindowItems
                  : row.map((_, index) => ({ kind: 'col' as const, index }))
                ).map((slot) => {
                  if (slot.kind === 'gap') {
                    return (
                      <TableCell
                        key={`hgap-${ri}-${slot.start}`}
                        colSpan={slot.span}
                        style={{
                          width: slot.width,
                          minWidth: slot.width,
                          maxWidth: slot.width,
                          padding: 0,
                          position: 'sticky',
                          top: 0,
                          zIndex: 3,
                          background: TABLE_HEAD_BG,
                        }}
                      />
                    )
                  }
                  const ci = slot.index
                  const col = row[ci]
                  if (!col || col.hidden) return null;
                  const hc = col.onHeaderCell?.(col);
                  const stickyOffset = resolveStickyOffset(col);
                  const base = cellStyle(col, col.fixed, stickyOffset, {
                    stickyRightEdge: isStickyRightEdge(col),
                    stickyLeftEdge: isStickyLeftEdge(col),
                    isHead: true,
                  });
                  return (
                    <TableCell
                      key={col.key ?? String(col.dataIndex) ?? ci}
                      colSpan={col.colSpan}
                      rowSpan={col.rowSpan}
                      align={col.align}
                      className={col.className ?? hc?.className}
                      style={{
                        ...base,
                        fontWeight: 600,
                        position: 'sticky',
                        top: 0,
                        ...(stickyOffset != null && (col.fixed === 'left' || col.fixed === true)
                          ? { left: stickyOffset }
                          : {}),
                        ...(stickyOffset != null && col.fixed === 'right' ? { right: stickyOffset } : {}),
                        zIndex: col.fixed ? 4 : 3,
                        background: TABLE_HEAD_BG,
                        padding,
                        ...(hc?.style || {}),
                      }}
                    >
                      {typeof col.title === 'function' ? col.title() : col.title}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            {hasSubHeader ? (
              <TableRow className="reality-allot-band">
                {rowSelection ? (
                  <TableCell
                    className="ant-table-selection-column"
                    padding="none"
                    style={{
                      width: selectionColWidth,
                      minWidth: selectionColWidth,
                      maxWidth: selectionColWidth,
                      position: 'sticky',
                      top: ALLOT_BAND_TOP,
                      left: expandColWidth,
                      zIndex: 5,
                      background: ALLOT_BAND_BG,
                      padding: 0,
                      boxShadow: leadingEdgeShadow,
                    }}
                  />
                ) : null}
                {(columnWindowItems && leafCols.length >= 40
                  ? columnWindowItems
                  : leafCols.map((_, index) => ({ kind: 'col' as const, index }))
                ).map((slot) => {
                  if (slot.kind === 'gap') {
                    return (
                      <TableCell
                        key={`agap-${slot.start}`}
                        colSpan={slot.span}
                        style={{
                          width: slot.width,
                          minWidth: slot.width,
                          maxWidth: slot.width,
                          padding: 0,
                          position: 'sticky',
                          top: ALLOT_BAND_TOP,
                          zIndex: 3,
                          background: ALLOT_BAND_BG,
                        }}
                      />
                    )
                  }
                  const col = leafCols[slot.index]
                  if (!col || col.hidden) return null
                  const stickyOffset = resolveStickyOffset(col)
                  const base = cellStyle(col, col.fixed, stickyOffset, {
                    stickyRightEdge: isStickyRightEdge(col),
                    stickyLeftEdge: isStickyLeftEdge(col),
                    isHead: true,
                  })
                  const node = col.subHeader
                  return (
                    <TableCell
                      key={`allot-${col.key ?? String(col.dataIndex) ?? slot.index}`}
                      align={col.align}
                      style={{
                        ...base,
                        position: 'sticky',
                        top: ALLOT_BAND_TOP,
                        ...(stickyOffset != null && (col.fixed === 'left' || col.fixed === true)
                          ? { left: stickyOffset }
                          : {}),
                        ...(stickyOffset != null && col.fixed === 'right' ? { right: stickyOffset } : {}),
                        zIndex: col.fixed ? 4 : 3,
                        background: ALLOT_BAND_BG,
                        padding,
                      }}
                    >
                      {typeof node === 'function' ? node() : node}
                    </TableCell>
                  )
                })}
              </TableRow>
            ) : null}
          </TableHead>
          {summaryTop ? (
            <TableBody className="ui-table-summary ui-table-summary--top" sx={summaryCellSx}>
              <SummaryStickyContext.Provider value={summaryStickyOf}>{summaryNode}</SummaryStickyContext.Provider>
            </TableBody>
          ) : null}
          <TableBody>
            {pagedData.map((record, index) => {
              const key = getKey(record, index);
              const rk = String(key);
              const rowCls =
                typeof rowClassName === 'function' ? rowClassName(record, index) : rowClassName;
              const rowProps = onRow?.(record, index) || {};
              const isExpanded = expanded[rk];
              const isSelected = selectedKeys.includes(key);
              const checkboxDisabled = rowSelection?.getCheckboxProps?.(record)?.disabled;

              const renderRow = () => (
                <>
                  <TableRow
                    className={rowCls}
                    hover
                    {...rowProps}
                    {...kiotExpandDataRowAttrs({ isExpandedWithPanel: !!isExpanded })}
                    data-expanded={isExpanded ? 'true' : undefined}
                    ref={(el: HTMLTableRowElement | null) => {
                      if (el) expandAnchorRowRefs.current.set(rk, el);
                      else expandAnchorRowRefs.current.delete(rk);
                    }}
                    sx={
                      isExpanded
                        ? {
                            bgcolor: TABLE_ROW_EXPANDED_BG,
                            '& > .MuiTableCell-root': {
                              bgcolor: `${TABLE_ROW_EXPANDED_BG} !important`,
                              borderBottom: 'none !important',
                            },
                          }
                        : undefined
                    }
                    onClick={(e) => {
                      rowProps.onClick?.(e);
                      if (expandable?.expandRowByClick) toggleExpand(record, index);
                    }}
                  >
                    {expandable?.expandedRowRender && (
                      <TableCell
                        className="ant-table-row-expand-icon-cell"
                        padding="none"
                        sx={{
                          width: expandColWidth,
                          minWidth: expandColWidth,
                          maxWidth: expandColWidth,
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          bgcolor: isExpanded ? TABLE_ROW_EXPANDED_BG : '#fff',
                          px: 0,
                          textAlign: 'center',
                          boxShadow: rowSelection ? undefined : leadingEdgeShadow,
                        }}
                      >
                        <IconButton
                          size="small"
                          aria-label={isExpanded ? 'Thu gọn' : 'Mở rộng'}
                          onClick={() => toggleExpand(record, index)}
                          sx={{ p: 0.25 }}
                        >
                          {isExpanded ? (
                            <KeyboardArrowDown fontSize="small" />
                          ) : (
                            <KeyboardArrowRight fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                    )}
                    {rowSelection && (
                      <TableCell
                        className="ant-table-selection-column"
                        padding="none"
                        sx={{
                          width: selectionColWidth,
                          minWidth: selectionColWidth,
                          maxWidth: selectionColWidth,
                          position: 'sticky',
                          left: expandColWidth,
                          zIndex: 2,
                          bgcolor: isExpanded ? TABLE_ROW_EXPANDED_BG : '#fff',
                          px: '2px',
                          py: '4px',
                          textAlign: 'center',
                          boxShadow: leadingEdgeShadow,
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          disabled={checkboxDisabled}
                          onChange={(e) => {
                            let keys = [...selectedKeysRef.current];
                            let rows = dataSourceRef.current.filter((r, i) => keys.includes(getKey(r, i)));
                            if (rowSelection.type === 'radio') {
                              keys = [key];
                              rows = [record];
                            } else if (e.target.checked) {
                              keys.push(key);
                              rows.push(record);
                            } else {
                              keys = keys.filter((k) => k !== key);
                              rows = rows.filter((r, i) => getKey(r, i) !== key);
                            }
                            rowSelection.onChange?.(keys, rows);
                          }}
                          sx={{ p: 0, m: 0, color: '#bdbdbd', '&.Mui-checked': { color: 'primary.main' } }}
                        />
                      </TableCell>
                    )}
                    {(columnWindowItems ?? leafCols.map((_, index) => ({ kind: 'col' as const, index }))).map((slot) => {
                      if (slot.kind === 'gap') {
                        return (
                          <TableCell
                            key={`bgap-${slot.start}`}
                            colSpan={slot.span}
                            style={{
                              width: slot.width,
                              minWidth: slot.width,
                              maxWidth: slot.width,
                              padding: 0,
                            }}
                          />
                        )
                      }
                      const ci = slot.index
                      const col = leafCols[ci]
                      if (!col) return null
                      const val = getByPath(record, col.dataIndex);
                      const cellExtra = col.onCell?.(record, index) || {};
                      if (cellExtra.colSpan === 0 || cellExtra.rowSpan === 0) return null;
                      const stickyOffset = resolveStickyOffset(col, ci);
                      return (
                        <TableCell
                          key={col.key ?? String(col.dataIndex) ?? ci}
                          align={col.align}
                          colSpan={cellExtra.colSpan}
                          rowSpan={cellExtra.rowSpan}
                          className={cellExtra.className ?? col.className}
                          style={{
                            ...cellStyle(col, col.fixed, stickyOffset, {
                              stickyRightEdge: isStickyRightEdge(col, ci),
                              stickyLeftEdge: isStickyLeftEdge(col, ci),
                              isHead: false,
                            }),
                            padding,
                            ...(cellExtra.style || {}),
                          }}
                        >
                          {col.render ? col.render(val, record, index) : val}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {expandable?.expandedRowRender && isExpanded && (
                    <KiotAntExpandPanel
                      colSpan={leafCols.length + (rowSelection ? 1 : 0) + 1}
                      scrollEl={scrollEl}
                      viewportW={viewportW}
                      anchorRowEl={expandAnchorRowRefs.current.get(rk) ?? null}
                    >
                      {expandable.expandedRowRender(record)}
                    </KiotAntExpandPanel>
                  )}
                </>
              );

              if (memoRows && !onRow) {
                return (
                  <MemoRow
                    key={rk}
                    render={renderRow}
                    deps={[
                      record, index, rowCls, isExpanded, isSelected, checkboxDisabled,
                      leafCols, columnWindowItems, fixedOffsets, padding, scrollEl, viewportW,
                    ]}
                  />
                );
              }
              return <React.Fragment key={rk}>{renderRow()}</React.Fragment>;
            })}
          </TableBody>
          {summaryNode != null && !summaryTop && (
            <TableFooter className="ui-table-summary" sx={summaryCellSx}>
              <SummaryStickyContext.Provider value={summaryStickyOf}>{summaryNode}</SummaryStickyContext.Provider>
            </TableFooter>
          )}
        </MuiTable>
        </ColumnWindowContext.Provider>
        {footer && (
          <Box sx={{ p: 1, borderTop: '1px solid #f0f0f0' }}>{footer()}</Box>
        )}
      </TableContainer>
      {showPag && !hidePaginationBar && pagEl}
    </Box>
  );
}

export type TableComponent = typeof TableInner & {
  Summary: typeof Summary;
};

export const Table = React.memo(TableInner) as unknown as TableComponent;
Table.Summary = Summary;

export default Table;
